import { findVerse } from '@rb/shared/admin-verses';
import { mergeManifestCollections } from '@rb/shared/master-data/merge-manifest';
import { CheckIcon, CopyIcon, ExternalLinkIcon, Loader2Icon, RefreshCwIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  cdnBaseUrl,
  cdnChannel,
  fetchManifest,
  fetchServerManifest,
  isManifestV2,
  type Manifest,
} from '@/client/cdn-sheets';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useActiveVerseStore } from '@/verse/active-verse.store';
import { EnvSelector, type EnvSchema, envSchemaForVerseEnv } from './EnvSelector';
import { SupabaseLoginGate } from './SupabaseLoginGate';
import { hasDecoder, tryDecodeCollection } from './lib/decode-server-state';

interface CollectionRow {
  name: string;
  version: string;
  hash?: string;
  url: string;
}

interface ViewerState {
  name: string;
  url: string;
  loading: boolean;
  text: string | null;
  parsed: unknown;
  error: string | null;
  bytes: number;
}

/**
 * Manifest 뷰어 — 선택한 env 의 R2 manifest(rb-sheets/<env>/manifest.json)를 읽어
 * 현재 적용(apply)된 모든 collection(시트 산출물)을 나열하고, 클릭하면 그 JSON 을
 * 같은 페이지 우측 뷰어에서 pretty-print 로 열람한다. (새 탭 X)
 */
export function ManifestPage() {
  const activeVerseId = useActiveVerseStore((s) => s.activeVerseId);
  const activeVerse = activeVerseId ? findVerse(activeVerseId) : null;
  const defaultEnv: EnvSchema = activeVerse ? envSchemaForVerseEnv(activeVerse.env) : 'dev';

  const [env, setEnv] = useState<EnvSchema>(defaultEnv);
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [rows, setRows] = useState<CollectionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<ViewerState | null>(null);

  const load = useCallback(async (targetEnv: EnvSchema) => {
    setLoading(true);
    setError(null);
    setViewer(null);
    try {
      const [clientM, serverM] = await Promise.all([
        fetchManifest(targetEnv),
        fetchServerManifest(targetEnv),
      ]);
      const m: Manifest =
        isManifestV2(clientM) && isManifestV2(serverM)
          ? mergeManifestCollections(clientM, serverM)
          : clientM;
      setManifest(m);
      if (isManifestV2(m)) {
        const list: CollectionRow[] = Object.entries(m.collections)
          .map(([name, meta]) => ({
            name,
            version: meta.version,
            hash: meta.hash,
            url: `${cdnBaseUrl()}/${cdnChannel(targetEnv)}/${name}.${meta.version}.json`,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setRows(list);
      } else {
        setRows([]);
      }
    } catch (e) {
      setManifest(null);
      setRows([]);
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(env);
  }, [env, load]);

  const openViewer = useCallback(async (row: CollectionRow) => {
    setViewer({
      name: row.name,
      url: row.url,
      loading: true,
      text: null,
      parsed: null,
      error: null,
      bytes: 0,
    });
    try {
      const res = await fetch(row.url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.text();
      let pretty = raw;
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(raw);
        pretty = JSON.stringify(parsed, null, 2);
      } catch {
        /* not JSON-parseable — show raw text */
      }
      setViewer({
        name: row.name,
        url: row.url,
        loading: false,
        text: pretty,
        parsed,
        error: null,
        bytes: new Blob([raw]).size,
      });
    } catch (e) {
      setViewer({
        name: row.name,
        url: row.url,
        loading: false,
        text: null,
        parsed: null,
        error: (e as Error).message,
        bytes: 0,
      });
    }
  }, []);

  const schemaVersion = manifest && isManifestV2(manifest) ? manifest.schemaVersion : null;
  const updatedAt = manifest && isManifestV2(manifest) ? manifest.updatedAt : undefined;
  const clientRows = rows.filter((r) => r.name.startsWith('client'));
  const serverRows = rows.filter((r) => r.name.startsWith('server'));
  const otherRows = rows.filter(
    (r) => !r.name.startsWith('client') && !r.name.startsWith('server')
  );

  return (
    <SupabaseLoginGate>
      <div className="flex h-[calc(100vh-32px)] flex-col gap-3 bg-background p-4 text-foreground">
        <div className="flex items-end gap-3">
          <EnvSelector value={env} onChange={setEnv} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => load(env)}
            disabled={loading}
          >
            {loading ? (
              <Loader2Icon className="size-4 animate-spin" />
            ) : (
              <RefreshCwIcon className="size-4" />
            )}
            새로고침
          </Button>
          <div className="ml-auto text-xs text-muted-foreground">
            {manifest && (
              <span>
                schemaVersion <span className="font-mono">{schemaVersion ?? 'v1'}</span> · updatedAt{' '}
                <span className="font-mono">{updatedAt || '—'}</span> · {rows.length} collections
              </span>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-rose-300/60 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300">
            manifest 로드 실패: {error}
          </div>
        )}

        {!error && rows.length === 0 && !loading && (
          <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
            collection 이 없습니다 (v1 manifest 이거나 아직 apply 되지 않음).
          </div>
        )}

        {rows.length > 0 && (
          <div className="grid min-h-0 flex-1 gap-3" style={{ gridTemplateColumns: '320px 1fr' }}>
            {/* Left: collection list */}
            <div className="flex min-h-0 flex-col gap-3 overflow-auto pr-1">
              <ManifestSection
                title={`Client (${clientRows.length})`}
                rows={clientRows}
                selected={viewer?.name ?? null}
                onSelect={openViewer}
              />
              <ManifestSection
                title={`Server (${serverRows.length})`}
                rows={serverRows}
                selected={viewer?.name ?? null}
                onSelect={openViewer}
              />
              <ManifestSection
                title={`Other (${otherRows.length})`}
                rows={otherRows}
                selected={viewer?.name ?? null}
                onSelect={openViewer}
              />
            </div>

            {/* Right: in-page JSON viewer */}
            <JsonViewer viewer={viewer} />
          </div>
        )}
      </div>
    </SupabaseLoginGate>
  );
}

function ManifestSection({
  title,
  rows,
  selected,
  onSelect,
}: {
  title: string;
  rows: CollectionRow[];
  selected: string | null;
  onSelect: (row: CollectionRow) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="rounded-md border border-border">
      <div className="border-b border-border bg-muted/40 px-3 py-1.5 text-xs font-medium text-foreground">
        {title}
      </div>
      <ul className="divide-y divide-border">
        {rows.map((r) => (
          <li key={r.name}>
            <button
              type="button"
              onClick={() => onSelect(r)}
              className={cn(
                'flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left hover:bg-muted/50',
                selected === r.name && 'bg-primary/10'
              )}
            >
              <span className="font-mono text-xs text-foreground">{r.name}</span>
              <span className="font-mono text-[10px] text-muted-foreground">
                v{r.version}
                {r.hash ? ` · ${r.hash.replace(/^sha256:/, '').slice(0, 8)}` : ''}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function JsonViewer({ viewer }: { viewer: ViewerState | null }) {
  const [copied, setCopied] = useState(false);
  const [decoded, setDecoded] = useState(true);

  const decodable = viewer != null && hasDecoder(viewer.name);

  const decodedText = useMemo(() => {
    if (!viewer || !decodable || viewer.parsed == null) return null;
    try {
      const out = tryDecodeCollection(viewer.name, viewer.parsed);
      return out == null ? null : JSON.stringify(out, null, 2);
    } catch (e) {
      return `// decode failed: ${(e as Error).message}\n${viewer.text ?? ''}`;
    }
  }, [viewer, decodable]);

  const displayText = decodable && decoded ? decodedText : viewer?.text;

  if (!viewer) {
    return (
      <div className="flex min-h-0 items-center justify-center rounded-md border border-border bg-muted/20 text-sm text-muted-foreground">
        왼쪽에서 collection 을 선택하면 여기서 JSON 을 열람합니다.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-col rounded-md border border-border">
      <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-2">
        <span className="font-mono text-xs font-medium text-foreground">{viewer.name}</span>
        {viewer.bytes > 0 && (
          <span className="text-[10px] text-muted-foreground">
            {(viewer.bytes / 1024).toLocaleString(undefined, { maximumFractionDigits: 1 })} KB
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {decodable && (
            <Button
              type="button"
              size="xs"
              variant={decoded ? 'default' : 'outline'}
              onClick={() => setDecoded((v) => !v)}
              aria-label="Toggle decoded view"
            >
              {decoded ? 'Decoded' : 'Raw'}
            </Button>
          )}
          {displayText && (
            <Button
              type="button"
              size="icon-xs"
              variant="outline"
              aria-label="Copy JSON"
              onClick={async () => {
                await navigator.clipboard.writeText(displayText);
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
              }}
            >
              {copied ? <CheckIcon className="size-3" /> : <CopyIcon className="size-3" />}
            </Button>
          )}
          <a
            href={viewer.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex size-6 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted"
            aria-label="Open raw in new tab"
          >
            <ExternalLinkIcon className="size-3" />
          </a>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto bg-muted/10">
        {viewer.loading && (
          <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" /> 불러오는 중…
          </div>
        )}
        {viewer.error && (
          <div className="p-4 text-sm text-rose-600 dark:text-rose-400">
            로드 실패: {viewer.error}
          </div>
        )}
        {displayText != null && (
          <pre className="whitespace-pre p-3 font-mono text-xs leading-relaxed text-foreground">
            {displayText}
          </pre>
        )}
      </div>
    </div>
  );
}

export default ManifestPage;
